import { Card } from '@/components/ui/card';
import { CheckCircle2, Heart, Shield, Leaf } from 'lucide-react';

export const AboutSection = () => {
  const values = [
    {
      icon: Heart,
      title: 'Customer-Centric',
      description: 'Every journey is designed around our clients\' unique needs and preferences.'
    },
    {
      icon: Shield,
      title: 'Integrity',
      description: 'Transparent pricing and honest recommendations you can trust.'
    },
    {
      icon: Leaf,
      title: 'Sustainability',
      description: 'Promoting eco-friendly travel and supporting local communities.'
    },
    {
      icon: CheckCircle2,
      title: 'Excellence',
      description: 'Delivering high-quality experiences from start to finish.'
    }
  ];

  const reasons = [
    'Experienced travel consultants with global expertise',
    '24/7 customer support during your trip',
    'Strong partnerships with hotels and tour operators',
    'Flexible payment plans and competitive rates',
    'Personalized itineraries tailored to your interests',
    'Commitment to sustainable and responsible tourism'
  ];

  return (
    <section id="about" className="section-padding bg-gradient-to-b from-background to-muted/30">
      <div className="container-custom">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Column - Image */}
          <div className="relative">
            <div className="relative rounded-2xl overflow-hidden shadow-xl">
              <img
                src="https://images.pexels.com/photos/59989/elephant-herd-of-elephants-african-bush-elephant-africa-59989.jpeg"
                alt="Elephant herd in Africa"
                className="w-full h-[500px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-foreground/60 to-transparent" />
              
              {/* Floating Stats Card */}
              <div className="absolute bottom-6 left-6 right-6 bg-background/95 backdrop-blur-sm rounded-xl p-6 shadow-lg">
                <p className="text-sm text-muted-foreground mb-2">Since 2020</p>
                <h3 className="text-2xl font-bold text-foreground font-serif mb-1">
                  Your Trusted Travel Partner
                </h3>
                <p className="text-sm text-muted-foreground">
                  Creating unforgettable African experiences
                </p>
              </div>
            </div>
          </div>

          {/* Right Column - Content */}
          <div className="space-y-8">
            <div>
              <span className="inline-block px-4 py-1 bg-primary/10 text-primary text-sm font-medium rounded-full mb-4">
                About Us
              </span>
              <h2 className="text-4xl sm:text-5xl font-bold text-foreground mb-6 font-serif leading-tight">
                Inspiring Connections Through Exceptional Travel
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed mb-6">
                Peregrine Tours & Safaris is a premier travel solutions provider dedicated to creating unforgettable journeys across Southern and Eastern Africa. Established in 2020, we specialize in tailor-made holiday packages, corporate travel management, and adventure expeditions.
              </p>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Our mission is to inspire and connect people through exceptional travel experiences that celebrate culture, nature, and human connection.
              </p>
            </div>

            {/* Core Values */}
            <div className="grid sm:grid-cols-2 gap-4">
              {values.map((value, index) => {
                const Icon = value.icon;
                return (
                  <Card key={index} className="p-5 border-2 hover:border-primary/30 transition-all duration-300 hover:shadow-md">
                    <div className="flex items-start space-x-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Icon className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-foreground mb-1">{value.title}</h4>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          {value.description}
                        </p>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>

            {/* Why Choose Us */}
            <div className="bg-accent/5 rounded-xl p-6 border border-accent/20">
              <h3 className="text-xl font-bold text-foreground mb-4 font-serif">
                Why Choose Peregrine Tours?
              </h3>
              <ul className="space-y-3">
                {reasons.map((reason, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                    <span className="text-muted-foreground">{reason}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};